import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import Layout from '~/components/Layout';
import RichTextRenderer from '~/components/Dynamic/RichTextRenderer';
import Seo from '~/components/SEO';
import SbEditable from 'storyblok-react';
import StoryPreview from '~/components/StoryPreview';
import SocialIcon from '~/components/SocialIcon';
import TextCard from '~/components/Dynamic/TextCard';
import Image from '~/components/Dynamic/Image';
import useStoryblok from '~/utils/storyblok-service';
import useCustomerPreviews from '~/hooks/useCustomerPreviews';
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton } from 'react-share';

function CustomerPost({ data, location, pageContext }) {
  const story = useStoryblok(data.storyblokEntry, location);

  const content = story.content;
  const hero = content.hero[0];
  const [pageUrl, setPageUrl] = useState('');

  const customersPreviews = useCustomerPreviews().filter(p => p.lang === pageContext.lang)
  const relatedPosts = customersPreviews.filter((p) => p.uuid !== data.storyblokEntry.uuid).slice(0, 2);

  useEffect(() => {
    setPageUrl(window?.location?.href);
  }, []);

  return (
    <Layout addBackgroundDefault locale={pageContext.locale}>
      <Seo {...content.seo} lang={story.lang} pageContext={pageContext} />
      <section className="pt-32 mb-20 -mt-16 md:mb-28 bg-beige">
        <div className="container">
          <div className="flex flex-wrap items-center -ml-16 pb-11">
            <div className="w-full pl-16 md:w-6/12">
              <div className="flex flex-col">
                {content?.logo && (
                  <div className="w-32 mx-auto mb-7 md:ml-0">
                    <Image blok={content?.logo} />
                  </div>
                )}
                <div className="text-center mb-14 md:text-left">
                  <TextCard
                    blok={{
                      ...hero?.textCard[0],
                      tag: 'h1',
                      headingClass:
                        hero?.textCard[0].headingClass ||
                        'font-bold leading-tight font-heading !text-36px md:!text-36px lg:!text-44px tracking-2',
                    }}
                  />
                </div>
                {/* <div>
                  <h4 className="font-extrabold uppercase text-16px font-heading">JAMES SMITH</h4>
                  <div className="italic font-bold mb-14">
                    <p>Head of operation, Company Name</p>
                  </div>
                </div> */}
              </div>
            </div>
            <div className="w-full pl-16 md:w-6/12">
              <Image blok={hero.asset[0]} />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap ">
          <div
            className={`w-full text-white  bg-blue py-9 ${
              content?.websiteUrl?.cached_url?.length > 0 ? 'md:w-3/4' : 'w-full'
            }`}
          >
            <div className="flex flex-wrap items-center justify-around space-y-6 text-center lg:flex-nowrap md:text-left md:space-y-0">
              {content.stats.map((stat) => (
                <SbEditable content={stat} key={stat._uid}>
                  <div className="flex flex-col w-full px-6 md:w-auto">
                    <h4 className="font-extrabold leading-snug  uppercase text-17px font-heading opacity-80 tracking-[0.01em] ">
                      {stat.heading}
                    </h4>
                    <h4 className="mt-1 font-semibold leading-snug text-20px tracking-2 font-heading">
                      {stat.content}
                    </h4>
                  </div>
                </SbEditable>
              ))}
            </div>
          </div>

          {content?.websiteUrl?.cached_url?.length > 0 && (
            <SbEditable content={content.websiteName}>
              <div className="w-full text-white bg-black md:w-1/4 py-9">
                <div className="flex flex-col items-center md:px-8 lg:px-16 md:items-start">
                  <h4 className="font-extrabold leading-snug  uppercase text-17px font-heading opacity-80 tracking-[0.01em] ">
                    {content.websiteHeading}
                  </h4>
                  <h4 className="mt-1 font-semibold leading-snug text-20px tracking-2 font-heading">
                    <a
                      href={content.websiteUrl.cached_url}
                      target="_blank"
                      className="underline transition-opacity duration-200 ease-in-out hover:opacity-50"
                    >
                      {content.websiteName}
                    </a>
                  </h4>
                </div>
              </div>
            </SbEditable>
          )}
        </div>
      </section>

      <section className="my-12 md:my-28">
        <div className="container">
          <div className="flex justify-center">
            <div className="flex flex-col items-center">
              <RichTextRenderer content={content.body} />
            </div>
          </div>

          <div className="flex items-center justify-center mt-12 md:mt-28">
            {pageContext?.locale?.content?.share || 'Share'}
            <div className="pl-3">
              <FacebookShareButton url={pageUrl}>
                <SocialIcon />
              </FacebookShareButton>
            </div>
            <div className="pl-3">
              <TwitterShareButton url={pageUrl} title={hero?.textCard[0]?.heading}>
                <SocialIcon type="twitter" />
              </TwitterShareButton>
            </div>
            <div className="pl-3">
              <LinkedinShareButton url={pageUrl} source={pageUrl} title={hero?.textCard[0]?.heading}>
                <SocialIcon type="linkedin" />
              </LinkedinShareButton>
            </div>
          </div>
        </div>
      </section>

      <div className="container my-12 border-t border-black md:my-28 opacity-10"></div>

      {relatedPosts?.length > 0 && (
        <section className="my-20 md:my-28">
          <div className="container">
            <h2 className="text-center text-h2 md:text-left">{pageContext?.locale?.content?.relatedStories}</h2>
            <div className="grid gap-10 mt-12 md:gap-16 md:grid-cols-2">
              {relatedPosts.map((post) => (
                <div key={post.uuid} className="flex flex-col">
                  <StoryPreview post={post} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
    //   <div className="max-w-588">
    //   <h3 className="mb-4 font-bold text-38px tracking-2 font-heading ">Heading 1</h3>
    //   <div className="leading-snug tracking-tighter prose text-black text-24px opacity-70">
    //     <p>
    //       Nulla vitae elit libero, a pharetra augue. Cras mattis consectetur purus sit amet fermentum vestibul
    //       umligula porta felis euismod semper maecenas.
    //     </p>
    //   </div>
    // </div>
    // <div className="mt-16 max-w-588">
    //   <h3 className="mb-4 font-bold text-28px tracking-2 font-heading ">Heading 2</h3>
    //   <div className="leading-normal tracking-tighter prose text-black text-17px opacity-70">
    //     <p>
    //       Nulla vitae elit libero, a pharetra augue. Cras mattis consectetur purus sit amet fermentum.
    //       Vestibulum id ligula porta felis euismod semper. Maecenas sed diam eget risus varius blandit sit
    //       amet non magna. Cras justo odio, dapibus ac facilisis in, egestas eget quam. Fusce dapibus, tellus
    //       ac cursus commodo, tortor mauris condimentum nibh, ut fermentum massa justo sit amet risus. Morbi
    //       leo risus, porta ac consectetur ac, vestibulum at eros. Fusce dapibus, tellus ac cursus commodo,
    //       tortor mauris condimentum nibh, ut fermentum massa justo sit amet risus. Etiam porta sem malesuada
    //       magna mollis euismod. Maecenas sed diam eget risus varius blandit sit amet non magna. Maecenas
    //       faucibus. Nulla vitae elit libero, a pharetra augue. Cras mattis consectetur purus sit amet
    //       fermentum. Vestibulum id ligula porta felis euismod semper. Maecenas sed diam eget risus varius
    //       blandit sit amet non magna. Cras justo odio, dapibus ac facilisis in, egestas eget quam.
    //     </p>
    //   </div>
    // </div>

    // <div className="max-w-2xl mt-16 rounded-xl">
    //   <StaticImage
    //     src={`../assets/images/customer-post/video-placeholder.jpg`}
    //     className="shadow-md rounded-xl"
    //     alt=""
    //   />
    // </div>

    // <div className="mt-20 max-w-588">
    //   <div>
    //     <p>
    //       Nulla vitae elit libero, a pharetra augue. Cras mattis consectetur purus sit amet fermentum.
    //       Vestibulum id ligula porta felis euismod semper. Maecenas sed diam eget risus varius blandit sit
    //       amet non magna. Cras justo odio, dapibus ac facilisis in, egestas eget quam.{' '}
    //     </p>
    //   </div>

    //   <div className="mt-12">
    //     <div className="border-t"></div>
    //     <div className="font-bold leading-normal tracking-tighter text-20px py-9">
    //       <p>
    //         Nulla vitae elit libero, a pharetra augue. Cras mattis consectetur purus sit amet fermentum.
    //         Vestibulum id ligula porta felis euismod semper. Maecenas sed diam eget risus varius.
    //       </p>
    //     </div>
    //     <div className="border-t"></div>
    //   </div>

    //   <div className="mt-16">
    //     <div className="font-semibold icon-list text-15px">
    //       <ul>
    //         <li>Start with 1 user</li>
    //         <li>Start with 1 user</li>
    //         <li>Start with 1 user</li>
    //         <li>Start with 1 user</li>
    //       </ul>
    //     </div>
    //   </div>
    // </div>
    // <div className="max-w-xl mt-16">
    //   <div>
    //     <StaticImage src={`../assets/images/customer-post/customer-post-place.jpg`} alt="" />
    //   </div>
    //   <div className="flex justify-center mt-4 font-bold leading-snug tracking-tighter uppercase opacity-60 text-14px">
    //     <p>Nulla vitae elitlibero a pharetra augue crasmattis consectetuR</p>
    //   </div>
    // </div>

    // <div className="mt-16 max-w-588">
    //   <h3 className="mb-4 font-extrabold text-28px tracking-2 font-heading ">Heading 2</h3>
    //   <div className="leading-normal tracking-tighter prose text-black text-17px opacity-70">
    //     <p>
    //       Nulla vitae elit libero, a pharetra augue. Cras mattis consectetur purus sit amet fermentum.
    //       Vestibulum id ligula porta felis euismod semper. Maecenas sed diam eget risus varius blandit sit
    //       amet non magna. Cras justo odio, dapibus ac facilisis in, egestas eget quam. Fusce dapibus, tellus
    //       ac cursus commodo, tortor mauris condimentum nibh, ut fermentum massa justo sit amet risus. Morbi
    //       leo risus, porta ac consectetur ac, vestibulum at eros. Fusce dapibus, tellus ac cursus commodo,
    //       tortor mauris condimentum nibh, ut fermentum massa justo sit amet risus. Etiam porta sem malesuada
    //       magna mollis euismod. Maecenas sed diam eget risus varius blandit sit amet non magna. Maecenas
    //       faucibus. Nulla vitae elit libero, a pharetra augue. Cras mattis consectetur purus sit amet
    //       fermentum. Vestibulum id ligula porta felis euismod semper. Maecenas sed diam eget risus varius
    //       blandit sit amet non magna. Cras justo odio, dapibus ac facilisis in, egestas eget quam.
    //     </p>
    //   </div>
    // </div>

    // <div className="relative max-w-[996px] bg-beige my-24">
    //   <div className="absolute inset-0 flex flex-col items-center justify-center ">
    //     <div className="relative w-full h-full">
    //       <div className="absolute bottom-[3%] left-[4%] lg:bottom-[9%] md:left-[4%]">
    //         <Star className="transform rotate-45" />
    //       </div>
    //       <div className="absolute top-[5%] right-[9%] md:top-[10%] md:right-[5%]">
    //         <Star />
    //       </div>
    //     </div>
    //   </div>
    //   <div className="flex flex-wrap p-6 py-24 md:p-20 md:-ml-20">
    //     <div className="flex justify-center w-full md:pl-20 lg:w-4/12">
    //       <StaticImage src={`../assets/images/customer-post/post-test.png`} alt="" />
    //     </div>
    //     <div className="flex justify-center w-full lg:w-8/12 md:pl-20">
    //       <div className="flex flex-col mt-12 lg:mt-0">
    //         <div>
    //           <h4 className="text-28px leading-[1.32] font-heading mt-8 md:mt-0">
    //             “BOOM makes everything easier, cheaper, faster. If it wasn’t for BOOM I’d still have to invest
    //             in marketing!”
    //           </h4>
    //         </div>
    //         <div className="flex flex-col justify-between mt-10 md:flex-row">
    //           <div>
    //             <img src={deliveroo} alt="" />
    //           </div>
    //           <div>
    //             <h4 className="mt-6 font-extrabold uppercase text-16px font-heading md:mt-0 ">JAMES SMITH</h4>
    //             <div className="italic font-bold">
    //               <p>Head of operation, Company Name</p>
    //             </div>
    //           </div>
    //         </div>
    //       </div>
    //     </div>
    //   </div>
    // </div>

    // <div className="leading-normal tracking-tighter prose text-black max-w-588 text-17px opacity-70">
    //   <p>
    //     Nulla vitae elit libero, a pharetra augue. Cras mattis consectetur purus sit amet fermentum.
    //     Vestibulum id ligula porta felis euismod semper. Maecenas sed diam eget risus varius blandit sit amet
    //     non magna. Cras justo odio, dapibus ac facilisis in, egestas eget quam. Fusce dapibus, tellus ac
    //     cursus commodo, tortor mauris condimentum nibh, ut fermentum massa justo sit amet risus. Morbi leo
    //     risus, porta ac consectetur ac, vestibulum at eros. Fusce dapibus, tellus ac cursus commodo, tortor
    //     mauris condimentum nibh.
    //   </p>
    // </div>

    // <div className="max-w-2xl my-16">
    //   <div>
    //     <StaticImage src={`../assets/images/customer-post/post-grey-place.jpg`} alt="" />
    //   </div>
    // </div>

    // <div className="leading-normal tracking-tighter prose text-black max-w-588 text-17px opacity-70">
    //   <p>
    //     Nulla vitae elit libero, a pharetra augue. Cras mattis consectetur purus sit amet fermentum.
    //     Vestibulum id ligula porta felis euismod semper. Maecenas sed diam eget risus varius blandit sit amet
    //     non magna. Cras justo odio, dapibus ac facilisis in, egestas eget quam.
    //   </p>
    // </div>

    // <div className="my-16 border-l-4 bg-light-grey border-blue max-w-588">
    //   <div className="px-16 py-11">
    //     <h4 className="font-bold text-22px font-heading tracking-[-0.01em] leading-snug">
    //       “BOOM makes everything easier, cheaper, faster. If it wasn’t for BOOM I’d still have to invest in
    //       marketing!”
    //     </h4>
    //   </div>
    // </div>

    // <div className="mt-16 max-w-588">
    //   <h3 className="mb-4 font-bold text-28px tracking-2 font-heading ">Heading 2</h3>
    //   <div className="leading-normal tracking-tighter prose text-black text-17px opacity-70">
    //     <p>
    //       Nulla vitae elit libero, a pharetra augue. Cras mattis consectetur purus sit amet fermentum.
    //       Vestibulum id ligula porta felis euismod semper. Maecenas sed diam eget risus varius blandit sit
    //       amet non magna. Cras justo odio, dapibus ac facilisis in, egestas eget quam. Fusce dapibus, tellus
    //       ac cursus commodo, tortor mauris condimentum nibh, ut fermentum massa justo sit amet risus. Morbi
    //       leo risus, porta ac consectetur ac, vestibulum at eros. Fusce dapibus, tellus ac cursus commodo,
    //       tortor mauris condimentum nibh, ut fermentum massa justo sit amet risus. Etiam porta sem malesuada
    //       magna mollis euismod. Maecenas sed diam eget risus varius blandit sit amet non magna. Maecenas
    //       faucibus. Nulla vitae elit libero, a pharetra augue. Cras mattis consectetur purus sit amet
    //       fermentum. Vestibulum id ligula porta felis euismod semper. Maecenas sed diam eget risus varius
    //       blandit sit amet non magna. Cras justo odio, dapibus ac facilisis in, egestas eget quam.
    //     </p>
    //   </div>
    // </div>

    // <div className="my-16 text-white bg-black max-w-588">
    //   <div className="flex flex-col p-8 md:p-20">
    //     <div className="font-bold leading-snug tracking-tight uppercase text-15px">title</div>
    //     <div className="mt-5 font-normal leading-normal tracking-tighter prose text-17px opacity-70">
    //       <p>
    //         Nulla vitae elit libero, a pharetra augue. Cras mattis consectetur purus sit amet fermentum.
    //         Vestibulum id ligula porta felis euismod semper. Maecenas sed diam eget risus varius blandit sit
    //         amet non magna. Cras justo odio, dapibus ac facilisis in.
    //       </p>
    //     </div>
    //     <div className="mt-12 font-semibold square-list--blue text-15px">
    //       <ul>
    //         <li>Start with 1 user</li>
    //         <li>Start with 1 user</li>
    //         <li>Start with 1 user</li>
    //         <li>Start with 1 user</li>
    //       </ul>
    //     </div>
    //   </div>
    // </div>
  );
}

export default CustomerPost;

export const query = graphql`
  query CustomerPostQuery($id: String!) {
    storyblokEntry(id: { eq: $id }) {
      id
      name
      content
      lang
      slug
      uuid
      full_slug
    }
  }
`;
